// /opt/isp_erp/frontend/src/components/ClienteBuscador.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, Popper, TextField, Typography } from '@mui/material';
import axios from '../api/axios';

const nombreCompleto = (c) => (c ? `${c.nombres || ''} ${c.apellidos || ''}`.trim() : '');
const detalle = (c) => [
  c.identificacion ? `Cédula ${c.identificacion}` : null,
  c.telefono ? `Tel. ${c.telefono}` : null,
  c.direccion || null,
].filter(Boolean).join(' · ');

// La lista se ensancha según su contenido (mínimo: el ancho del campo y 340 px) para que el nombre se lea completo en horizontal
const ListaAncha = (props) => {
  const ancho = props.style && typeof props.style.width === 'number' ? props.style.width : 0;
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{
        ...props.style,
        width: 'max-content',
        minWidth: `min(max(${ancho}px, 340px), 92vw)`,
        maxWidth: 'min(640px, 92vw)',
      }}
    />
  );
};

/**
 * Buscador de clientes que consulta al servidor mientras escribes (no descarga la lista completa).
 * value = cliente elegido (objeto) o null. onChange(cliente | null).
 * estados = "ACTIVO,SUSPENDIDO" limita la búsqueda (opcional).
 */
const ClienteBuscador = ({
  value, onChange, estados = '', label = 'Cliente', placeholder = 'Nombre, apellido, cédula o teléfono',
  helperText = '', disabled = false, required = false, size = 'medium', autoFocus = false,
}) => {
  const [texto, setTexto] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const contador = useRef(0);

  useEffect(() => {
    const termino = texto.trim();
    if (termino.length < 2) { setOpciones([]); setCargando(false); return undefined; }
    const id = ++contador.current;
    const espera = setTimeout(async () => {
      setCargando(true);
      try {
        const params = { search: termino, limit: 20 };
        if (estados) params.estados = estados;
        const r = await axios.get('/customers/search', { params });
        if (id === contador.current) setOpciones(r.data || []);
      } catch (e) {
        if (id === contador.current) setOpciones([]);
      } finally {
        if (id === contador.current) setCargando(false);
      }
    }, 300);
    return () => clearTimeout(espera);
  }, [texto, estados]);

  const lista = value && !opciones.some((o) => o.id === value.id) ? [value, ...opciones] : opciones;
  // Con un cliente elegido, debajo del campo se ve su información completa
  const ayuda = value ? [nombreCompleto(value), detalle(value)].filter(Boolean).join(' · ') : helperText;

  return (
    <Autocomplete
      fullWidth
      size={size}
      options={lista}
      value={value || null}
      loading={cargando}
      disabled={disabled}
      PopperComponent={ListaAncha}
      filterOptions={(o) => o}
      onChange={(_, v) => onChange(v || null)}
      onInputChange={(_, v, motivo) => {
        if (motivo === 'input') setTexto(v);
        if (motivo === 'clear') setTexto('');
      }}
      getOptionLabel={(c) => (c ? (nombreCompleto(c) || c.identificacion || `#${c.id}`) : '')}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={texto.trim().length < 2 ? 'Escribe al menos 2 letras' : (cargando ? 'Buscando...' : 'Sin resultados')}
      ListboxProps={{ style: { maxHeight: 380 } }}
      renderOption={(props, c) => (
        <li {...props} key={c.id}>
          <Box sx={{ width: '100%', py: 0.25 }}>
            <Typography component="div" variant="body1" sx={{ fontWeight: 'bold', whiteSpace: 'normal', overflowWrap: 'break-word' }}>
              {nombreCompleto(c)}
              <Typography component="span" variant="caption" color="text.secondary">{` #${c.id}`}</Typography>
              {c.estado && (
                <Chip size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 11 }}
                      label={c.estado === 'PENDIENTE_INSTALACION' ? 'PENDIENTE' : c.estado} />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', overflowWrap: 'break-word' }}>
              {detalle(c)}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={ayuda || undefined}
          FormHelperTextProps={{ sx: { whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: 1.35 } }}
          required={required}
          autoFocus={autoFocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (<>{cargando ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>),
          }}
        />
      )}
    />
  );
};

export default ClienteBuscador;
